import {
  Application,
  ApplicationStatus as UIApplicationStatus,
} from "@/entities/application/model/types";
import { centsToDollars, dollarsToCents } from "@/shared/lib/format";
import {
  ApplicationStatus as APIApplicationStatus,
  ApplicationCreateSchema,
  ApplicationOutputSchema,
  ApplicationShortOutputSchema,
  ApplicationUpdateSchema,
} from "./applications";

/**
 * Map API ApplicationOutputSchema to UI Application type
 */
export function mapApplicationToUI(apiApplication: ApplicationOutputSchema): Application {
  const name =
    apiApplication.account?.name ||
    `${apiApplication.configuration?.applicationStatus || "Unknown"} Application ${apiApplication.internalId.slice(0, 8)}`;

  return {
    id: apiApplication.internalId,
    externalId: apiApplication.externalId,
    name,
    email: undefined,
    amount: centsToDollars(apiApplication.amount),
    status: normalizeApplicationStatus(apiApplication.status),
    location: apiApplication.locationName,
    createdAt: apiApplication.createdAt,
  };
}

/**
 * Map API ApplicationShortOutputSchema to UI Application type
 */
export function mapApplicationShortToUI(apiApplication: ApplicationShortOutputSchema): Application {
  const name = `${apiApplication.firstName} ${apiApplication.lastName}`.trim();

  return {
    id: apiApplication.internalId,
    externalId: apiApplication.externalId,
    name,
    firstName: apiApplication.firstName,
    lastName: apiApplication.lastName,
    email: apiApplication.email,
    amount: centsToDollars(apiApplication.amount),
    status: normalizeApplicationStatus(apiApplication.status),
    location: apiApplication.locationName,
    createdAt: apiApplication.createdAt,
  };
}

/**
 * Normalize application status from API to UI format
 */
function normalizeApplicationStatus(status: string): UIApplicationStatus {
  if (status === "KBA") {
    return "kba";
  }
  return status as UIApplicationStatus;
}

/**
 * Map UI Application to API ApplicationUpdateSchema
 */
export function mapApplicationToUpdate(application: Partial<Application>): ApplicationUpdateSchema {
  let apiStatus: APIApplicationStatus | null = null;
  if (application.status) {
    if (application.status === "kba") {
      apiStatus = "KBA";
    } else {
      apiStatus = application.status as APIApplicationStatus;
    }
  }

  return {
    status: apiStatus,
    amount: application.amount !== undefined ? dollarsToCents(application.amount) : null,
  };
}

/**
 * Map UI Application to API ApplicationCreateSchema
 * Note: This is a partial mapping - applicant data needs to be provided separately
 */
export function mapApplicationToCreate(
  application: Partial<Application>,
  applicant: {
    firstName: string;
    lastName: string;
    email: string;
    address1: string;
    address2?: string | null;
    city: string;
    state: string;
    zipCode?: string | null;
    SSN?: string | null;
    DOB?: string | null;
    phone?: string | null;
  }
): ApplicationCreateSchema {
  return {
    applicant,
    amount: dollarsToCents(application.amount) || 0,
    address2: null,
    qaNotes: null,
    accountId: null,
    webhook: null,
  };
}
